/**
 * Asking the engine to draw an ossia as an ossia.
 *
 * The document keeps an ossia as a voice of a bar and MusicXML carries it as a staff of the
 * part, written in every bar so that a reader takes the staves of a bar in the order they
 * arrive -- see `musicxml/export-ossia.ts`. Neither says what it *is*: MusicXML has
 * `<staff-type>ossia</staff-type>`, and Verovio does not read it -- verified against 6.3.0,
 * whose MusicXML importer takes the staff's size and ignores its type. MEI has the element,
 * so the same double load condensing uses is used here: the score goes in as the MusicXML
 * every other feature goes through, the engine hands its own MEI back, each ossia staff is
 * wrapped in `<ossia>`, and the result goes back in.
 *
 * The bars where nothing is offered are the other half of it. The file writes the staff in
 * every bar, so the engine draws a small empty staff running the length of the piece unless
 * it is taken out again, and taking it out is what makes an ossia appear over the one bar it
 * belongs to. Which bars those are is read from the document rather than sniffed out of the
 * MEI: the document is what the file was written from, and it says so exactly.
 *
 * An engine that cannot hand its MEI back draws the staff as the file states it: small,
 * present in every bar, and holding nothing. That is the safe way to fail -- the music is
 * all there and every bar is still clickable.
 */

import type { Score } from "../document/types";
import { staffMap } from "../musicxml/export-ossia";

/** Where a score's ossia staves are, in the numbers the engine's own MEI gives staves. */
export interface OssiaPlan {
  /** Every ossia staff of the score, numbered as MEI numbers staves across the system. */
  readonly staves: ReadonlySet<number>;
  /** The ossia staves that offer something, by bar in document order. */
  readonly offered: readonly ReadonlySet<number>[];
}

/**
 * Where the ossias of a score are drawn.
 *
 * MEI numbers the staves of a system straight through, part after part, so a part's own
 * numbering -- which is what the MusicXML says and what `staffMap` answers -- is offset by
 * every staff declared above it, ossia staves counted in.
 */
export function ossiaPlan(score: Score): OssiaPlan {
  const staves = new Set<number>();
  const offered = score.measures.map(() => new Set<number>());
  let base = 0;
  for (const part of score.parts) {
    const map = staffMap(part);
    map.ossia.forEach((number, staff) => {
      const drawn = base + number;
      staves.add(drawn);
      score.measures.forEach((global, index) => {
        const measure = part.measures[global.id];
        if (measure === undefined) return;
        if (measure.voices.some((voice) => voice.ossia && voice.staff === staff)) {
          offered[index]?.add(drawn);
        }
      });
    });
    base += map.count;
  }
  return { staves, offered };
}

/** One `<staff n="...">` element of the MEI: where it begins, where it ends, which staff. */
interface StaffSpan {
  readonly start: number;
  readonly end: number;
  readonly number: number;
}

/** Every `<staff>` element between two offsets, in order. `<staffDef>` is not one of them. */
function stavesWithin(mei: string, from: number, to: number): StaffSpan[] {
  const spans: StaffSpan[] = [];
  let at = mei.indexOf("<staff ", from);
  while (at >= 0 && at < to) {
    const open = mei.indexOf(">", at);
    if (open < 0) break;
    const tag = mei.slice(at, open + 1);
    const number = Number(/\bn="(\d+)"/.exec(tag)?.[1] ?? "0");
    const end = tag.endsWith("/>") ? open + 1 : mei.indexOf("</staff>", open) + "</staff>".length;
    if (end < open) break;
    spans.push({ start: at, end, number });
    at = mei.indexOf("<staff ", end);
  }
  return spans;
}

/** Where each `<measure>` of the MEI begins, in document order. */
function measureStarts(mei: string): number[] {
  const starts: number[] = [];
  let at = mei.indexOf("<measure");
  while (at >= 0) {
    starts.push(at);
    at = mei.indexOf("<measure", at + 1);
  }
  return starts;
}

/**
 * The engine's own MEI, with each ossia staff wrapped in `<ossia>` or taken out.
 *
 * The MEI is edited rather than parsed, as the condensing flag is, because one element is
 * being moved and the rest of the document -- ids, beams, spacing, everything the engine
 * worked out -- has to come back untouched. A document whose bars do not line up with the
 * score's is handed back as it came: drawing the ossia staff plainly is better than moving
 * the wrong bar's music.
 */
export function withOssia(mei: string, plan: OssiaPlan): string {
  if (plan.staves.size === 0) return mei;
  const starts = measureStarts(mei);
  if (starts.length !== plan.offered.length) return mei;

  const pieces: string[] = [];
  let taken = 0;
  starts.forEach((start, index) => {
    const to = starts[index + 1] ?? mei.length;
    const here = plan.offered[index] ?? new Set<number>();
    for (const span of stavesWithin(mei, start, to)) {
      if (!plan.staves.has(span.number)) continue;
      pieces.push(mei.slice(taken, span.start));
      if (here.has(span.number)) {
        pieces.push(`<ossia>${mei.slice(span.start, span.end)}</ossia>`);
      }
      taken = span.end;
    }
  });
  pieces.push(mei.slice(taken));
  return pieces.join("");
}
